import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import {
  Image,
  Tabs,
  Typography,
  Row,
  Col,
  Card,
  Empty,
  Steps,
  Progress,
  App,
  Collapse,
  Pagination,
  Flex,
  Tag,
} from "antd";
import CustomButton from "../../components/CustomButton/Button";
import ArrowDownIcon from "../../assets/icons/arrow-down-icon.svg";
import QuestionCardModal from "./QuestionCardModal";
import SubmittedQuestionModal from "./SubmittedQuestionModal";
import {
  getBusQustions,
  getAadhaarAndPan,
  submitBusQustions,
  resetQuestions,
} from "../../redux/slices/QuestionnaireSlice";
import {
  getPendingSlaInvitation,
  getPendingNdaInvitation,
} from "../../redux/slices/EsignSlice";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams, useNavigate, useLocation } from "react-router-dom";
import Loader from "../../components/CustomLoader/Loader";
import { TABLE_PAGE_SIZE } from "../../constants/tableConfig";
import { getJvQuestionDisplay, dedupeOrganizationQuestions } from "../../helpers/jvQuestionnaireAnswers";
import { isQuestionnaireQuestionOptional } from "../../helpers/questionnaireOptional";
import { GLOBAL_CONSTANTS } from "../../constants/constants";
import "./QuestionBank.scss";

const { Title, Text } = Typography;

const steps = [
  {
    title: "Business Request",
  },
  {
    title: "KYC Verification",
  },
  {
    title: "Review KYC",
  },
  {
    title: "Enterprise Onboarding & Creditlimit Finalization",
  },
  {
    title: "Review Credit Assessment",
  },
  {
    title: "Activate Business Account",
  },
];

const panelStyle = {
  marginBottom: 24,
  borderRadius: 8,
  border: "1px solid #D8E4FD",
};

const QuestionBank = () => {
  const dispatch = useDispatch();
  const { message } = App.useApp();
  const [selectedTab, setSelectedTab] = useState("PENDING");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [submittedModalOpen, setSubmittedModalOpen] = useState(false);
  const [selectedQuestion, setSelectedQuestion] = useState(null);
  const [questionPage, setQuestionPage] = useState(1);
  const { questionsData, onboardingStepStatus, aadhaarPanLoading } =
    useSelector((state) => state.questionnaire);
  const { user } = useSelector((state) => state.auth);
  const { pendingSla, pendingNda } = useSelector((state) => state.esign);
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const params = searchParams.get("status");
  const handledDigilockerCallback = useRef(null);
  const handledKycSuccessToast = useRef(false);
  const { activeCategory } = useSelector((state) => state.dealCategory);
  const resolvedDealCategoryId =
    activeCategory?.dealCategoryId ||
    user?.dealCategories?.find((c) => c?.dealCategoryId === "DCT1000002")
      ?.dealCategoryId ||
    user?.dealCategories?.[0]?.dealCategoryId;
  const isIF = resolvedDealCategoryId === "DCT1000001";
  const isJV = resolvedDealCategoryId === "DCT1000002";
  // SLA/NDA are JV-only docs — never show under Invoice Financing
  const showSlaSigned = isJV && pendingSla?.isSent && pendingSla?.isSigned;
  const showNdaSigned = isJV && pendingNda?.isSent && pendingNda?.isSigned;


  useEffect(() => {
    if (user?.orgId && resolvedDealCategoryId) {
      dispatch(
        getBusQustions({
          orgId: user.orgId,
          status: selectedTab,
          dealCategoryId: resolvedDealCategoryId,
        }),
      );
      if (isJV) {
        dispatch(getPendingSlaInvitation());
        dispatch(getPendingNdaInvitation());
      }
    }
  }, [dispatch, user, selectedTab, resolvedDealCategoryId, isJV]);

  useEffect(() => {
    setQuestionPage(1);
  }, [selectedTab]);

  useEffect(() => {
    if (handledKycSuccessToast.current) return;

    const fromState = Boolean(location.state?.kycSuccess);
    const fromQuery = searchParams.get("kycSuccess") === "1";
    if (!fromState && !fromQuery) return;

    handledKycSuccessToast.current = true;
    message.success(GLOBAL_CONSTANTS.KYC_SUCCESS_MSG);

    if (fromState) {
      navigate(location.pathname, { replace: true, state: {} });
    }
    if (fromQuery) {
      const nextParams = new URLSearchParams(searchParams);
      nextParams.delete("kycSuccess");
      setSearchParams(nextParams, { replace: true });
    }
  }, [
    location.pathname,
    location.state,
    message,
    navigate,
    searchParams,
    setSearchParams,
  ]);

  const threads = Array.isArray(questionsData)
    ? questionsData.filter(Boolean)
    : [];

  const questions = useMemo(
    () =>
      dedupeOrganizationQuestions(
        threads.flatMap((t) =>
          (Array.isArray(t.questions) ? t.questions : []).map((q) => ({
            ...q,
            threadId: q.threadId || t.threadId,
            threadName: q.threadName || t.threadName,
          })),
        ),
      ),
    [threads],
  );

  const paginatedQuestions = useMemo(() => {
    const start = (questionPage - 1) * TABLE_PAGE_SIZE;
    return questions.slice(start, start + TABLE_PAGE_SIZE);
  }, [questions, questionPage]);

  const isDirPanAadhaarQuestion = useMemo(
    () => questions.find((q) => q.questionId === "QBM1000043"),
    [questions]
  );

  useEffect(() => {
    const clearDigilockerCallbackParams = () => {
      const nextParams = new URLSearchParams(searchParams);
      nextParams.delete("status");
      nextParams.delete("orgId");
      nextParams.delete("dealCategoryId");
      nextParams.delete("state");
      setSearchParams(nextParams, { replace: true });
    };

    const fetchAadhaarPan = async () => {
      try {
        const response = await dispatch(
          getAadhaarAndPan({
            orgId: user?.orgId,
            dealCategoryId: activeCategory?.dealCategoryId,
          }),
        );

        if (response?.payload?.statusCode === 201) {
          const { aadhaar, pan } = response.payload.response;
          const payload = {
            threadQuestionId: isDirPanAadhaarQuestion.threadQuestionId,
            questionId: isDirPanAadhaarQuestion.questionId,
            orgId: user.orgId,
            answerText: `${aadhaar},${pan}`,
            dealCategoryId: activeCategory?.dealCategoryId,
          };
          const res = await dispatch(submitBusQustions(payload));
          if (res.payload.statusCode === 201) {
            dispatch(
              getBusQustions({
                orgId: user.orgId,
                status: "PENDING",
                dealCategoryId: activeCategory?.dealCategoryId,
              }),
            );
          } else {
            message.error(res?.payload);
          }
        } else {
          message.error(response?.payload);
        }
      } finally {
        clearDigilockerCallbackParams();
      }
    };

    const callbackKey = [
      params,
      user?.orgId,
      activeCategory?.dealCategoryId,
      isDirPanAadhaarQuestion?.threadQuestionId,
    ].join(":");

    if (
      params === "digilocker-consent-success" &&
      isDirPanAadhaarQuestion &&
      selectedTab === "PENDING" &&
      handledDigilockerCallback.current !== callbackKey
    ) {
      handledDigilockerCallback.current = callbackKey;
      fetchAadhaarPan();
    }
  }, [
    dispatch,
    user?.orgId,
    params,
    isDirPanAadhaarQuestion,
    message,
    activeCategory?.dealCategoryId,
    selectedTab,
    searchParams,
    setSearchParams,
  ]);

  const currentStep = useMemo(() => {
    if (!onboardingStepStatus) return 1;

    if (
      onboardingStepStatus.Business_Active &&
      onboardingStepStatus.Credit_Assessment_Complete
    )
      return 6;
    if (onboardingStepStatus.KYC_Verified) return 3;
    if (onboardingStepStatus.KYC_Review) return 2;
    if (onboardingStepStatus.KYC_Pending) return 1;

    return 1;
  }, [onboardingStepStatus]);

  const stepItems = useMemo(
    () => steps.map((step) => ({ title: step.title })),
    []
  );

  const progress = useMemo(() => {
    return Math.round((currentStep / steps.length) * 100);
  }, [currentStep]);

  const handleCardClick = useCallback((question) => {
    if (question.status === "ANSWERED" || question.status === "APPROVED") {
      setSelectedQuestion(question);
      setSubmittedModalOpen(true);
    } else {
      setSelectedQuestion(question);
      setIsModalOpen(true);
    }
  }, []);

  const closeModal = useCallback(() => {
    setIsModalOpen(false);
    setSelectedQuestion(null);
  }, []);

  const handleTabChange = useCallback(
    (val) => {
      setSelectedTab(val);
      dispatch(resetQuestions());
    },
    [dispatch]
  );

  const { groupedByDeal, questionsWithoutDeal } = useMemo(() => {
    if (!threads.length) {
      return { groupedByDeal: {}, questionsWithoutDeal: [] };
    }

    return threads.reduce(
      (acc, thread) => {
        const threadQuestions = Array.isArray(thread.questions)
          ? thread.questions
          : [];

        if (thread.dealId) {
          if (!acc.groupedByDeal[thread.dealId]) {
            acc.groupedByDeal[thread.dealId] = [];
          }
          acc.groupedByDeal[thread.dealId] = dedupeOrganizationQuestions([
            ...acc.groupedByDeal[thread.dealId],
            ...threadQuestions.map((q) => ({
              ...q,
              threadId: q.threadId || thread.threadId,
              threadName: q.threadName || thread.threadName,
            })),
          ]);
        } else {
          acc.questionsWithoutDeal = dedupeOrganizationQuestions([
            ...acc.questionsWithoutDeal,
            ...threadQuestions.map((q) => ({
              ...q,
              threadId: q.threadId || thread.threadId,
              threadName: q.threadName || thread.threadName,
            })),
          ]);
        }

        return acc;
      },
      { groupedByDeal: {}, questionsWithoutDeal: [] },
    );
  }, [threads]);

  const hasDealGroups = Object.keys(groupedByDeal).length > 0;

  const getButtonLabel = (status) => {
    switch (status) {
      case "PENDING":
        return "Answer";

      case "ANSWERED":
        return "View Submission";

      case "APPROVED":
        return "View Details";

      default:
        return "Open";
    }
  };

  const getButtonModel = (status) => {
    switch (status) {
      case "PENDING":
        return {
          color: "primary",
          variant: "solid",
        };

      case "ANSWERED":
        return {
          color: "primary",
          variant: "outlined",
        };

      case "APPROVED":
        return {
          color: "green",
          variant: "outlined",
        };

      default:
        return {
          color: "primary",
          variant: "solid",
        };
    }
  };

  const renderTaskCard = useCallback((question) => {
    const displayQuestion = getJvQuestionDisplay(question);
    const buttonModel = getButtonModel(displayQuestion.status);

    return (
      <Col xs={24} key={question.threadQuestionId || question.questionId}>
        <Card className="question-bank-task-card" variant="outlined" hoverable>
          <Row
            justify="space-between"
            align="middle"
            wrap={false}
            gutter={[16, 16]}
          >
            <Col flex="auto">
              <Row align="top" gutter={12} wrap={false}>
                <Col flex="auto">
                  <Title
                    level={5}
                    style={{
                      marginBottom: 4,
                      fontWeight: 600,
                    }}
                  >
                    {displayQuestion.questionTitle}
                    {isQuestionnaireQuestionOptional(displayQuestion) && (
                      <Tag color="blue" style={{ marginLeft: 8, fontWeight: 500 }}>
                        Optional
                      </Tag>
                    )}
                  </Title>

                  <Text
                    className="question-bank-task-description"
                    style={{
                      color: "#667085",
                      fontSize: "13px",
                    }}
                  >
                    {displayQuestion.description}
                  </Text>
                </Col>
              </Row>
            </Col>

            <Col>
              <CustomButton
                onClick={() => handleCardClick(displayQuestion)}
                variant={buttonModel.variant}
                color={buttonModel.color}
              >
                {getButtonLabel(displayQuestion.status)}
              </CustomButton>
            </Col>
          </Row>
        </Card>
      </Col>
    );
  }, [handleCardClick]);

  const renderFlatQuestionList = useMemo(
    () => (
      <>
        <Row gutter={[16, 16]}>{paginatedQuestions.map(renderTaskCard)}</Row>
        {questions.length > TABLE_PAGE_SIZE && (
          <Pagination
            current={questionPage}
            pageSize={TABLE_PAGE_SIZE}
            total={questions.length}
            onChange={setQuestionPage}
            style={{ marginTop: 16, textAlign: "center" }}
            showSizeChanger={false}
          />
        )}
      </>
    ),
    [paginatedQuestions, questions.length, questionPage, renderTaskCard]
  );

  const renderGroupedQuestions = useMemo(
    () => (
      <>
        {questionsWithoutDeal.length > 0 && (
          <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
            {questionsWithoutDeal.map(renderTaskCard)}
          </Row>
        )}
        {hasDealGroups && (
          <Collapse
            bordered={false}
            expandIconPosition="end"
            className="deal-collapse"
            expandIcon={({ isActive }) => (
              <Image
                src={ArrowDownIcon}
                alt="Arrow"
                preview={false}
                style={{
                  transform: isActive ? "rotate(180deg)" : "rotate(0deg)",
                  transition: "transform 0.3s ease",
                }}
              />
            )}
            items={Object.entries(groupedByDeal).map(([dealId, dealQuestions]) => ({
              key: dealId,
              label: `Deal ID: ${dealId}`,
              children: (
                <Row gutter={[16, 16]}>
                  {dealQuestions.map(renderTaskCard)}
                </Row>
              ),
              style: panelStyle,
            }))}
          />
        )}
      </>
    ),
    [groupedByDeal, hasDealGroups, questionsWithoutDeal, renderTaskCard],
  );

  const renderQuestionContent = useMemo(() => {
    if (!questions.length) return null;
    if (hasDealGroups) {
      return renderGroupedQuestions;
    }
    return renderFlatQuestionList;
  }, [
    questions.length,
    hasDealGroups,
    renderGroupedQuestions,
    renderFlatQuestionList,
  ]);

  const tabItems = useMemo(
    () => [
      {
        key: "PENDING",
        label: `To-Do`,
        children: questions.length ? (
          renderQuestionContent
        ) : (
          <Empty description="No pending tasks" />
        ),
      },
      {
        key: "ANSWERED",
        label: `Submitted`,
        children: questions.length ? (
          renderQuestionContent
        ) : (
          <Empty description="No submitted tasks" />
        ),
      },
      {
        key: "APPROVED",
        label: `Done`,
        children: questions.length ? (
          renderQuestionContent
        ) : (
          <Empty description="No completed tasks" />
        ),
      },
    ],
    [questions.length, renderQuestionContent],
  );

  return (
    <div className="question-bank-page">
      <div className="question-bank-header">
        <Title level={2}>Deal Onboarding Tasks</Title>
        <Text>
          Complete these tasks assigned by the admin to proceed with your deal
        </Text>
      </div>

      {selectedTab === "APPROVED" && (showSlaSigned || showNdaSigned) && (
        <div style={{ marginBottom: 16 }}>
          {showSlaSigned && (
            <Card className="question-card" style={{ marginBottom: 12 }}>
              <Flex justify="space-between" align="center" wrap="wrap" gap={12}>
                <div>
                  <Title level={5} style={{ marginBottom: 4 }}>
                    Service Level Agreement (SLA)
                  </Title>
                  <Text type="secondary">
                    SLA has been signed successfully.
                  </Text>
                </div>
                <Flex gap={8} wrap="wrap">
                  {pendingSla?.docUrl && (
                    <CustomButton
                      color="green"
                      variant="outlined"
                      onClick={() =>
                        window.open(pendingSla.docUrl, "_blank", "noopener,noreferrer")
                      }
                    >
                      View Document
                    </CustomButton>
                  )}
                  <CustomButton
                    color="green"
                    variant="outlined"
                    onClick={() => navigate("/sla")}
                  >
                    View Details
                  </CustomButton>
                </Flex>
              </Flex>
            </Card>
          )}
          {showNdaSigned && (
            <Card className="question-card">
              <Flex justify="space-between" align="center" wrap="wrap" gap={12}>
                <div>
                  <Title level={5} style={{ marginBottom: 4 }}>
                    Non-Disclosure Agreement (NDA)
                  </Title>
                  <Text type="secondary">
                    NDA has been signed successfully.
                  </Text>
                </div>
                <Flex gap={8} wrap="wrap">
                  {pendingNda?.docUrl && (
                    <CustomButton
                      color="green"
                      variant="outlined"
                      onClick={() =>
                        window.open(pendingNda.docUrl, "_blank", "noopener,noreferrer")
                      }
                    >
                      View Document
                    </CustomButton>
                  )}
                  <CustomButton
                    color="green"
                    variant="outlined"
                    onClick={() => navigate("/nda")}
                  >
                    View Details
                  </CustomButton>
                </Flex>
              </Flex>
            </Card>
          )}
        </div>
      )}

      <Row gutter={24}>
        {/* LEFT SIDE */}
        <Col xs={24} md={isIF && 16} lg={isIF && 18}>
          {aadhaarPanLoading ? (
            <Loader />
          ) : (
            <Tabs
              activeKey={selectedTab}
              onChange={handleTabChange}
              items={tabItems}
            />
          )}
        </Col>

        {isIF && (
          <Col xs={24} md={8} lg={6}>
            <div className="question-bank-stepper">
              <Title level={4}>Onboarding Steps</Title>
              <Text type="secondary" style={{ marginBottom: 10 }}>
                Steps progress
              </Text>
              <Progress
                percent={progress}
                size="small"
                style={{ marginBottom: 10 }}
              />
              <Steps
                direction="vertical"
                size="small"
                current={Math.min(Math.max(currentStep, 1), stepItems.length)}
                items={stepItems}
              />
            </div>
          </Col>
        )}
      </Row>

      <QuestionCardModal
        open={isModalOpen}
        onClose={closeModal}
        selectedQuestion={selectedQuestion}
      />

      <SubmittedQuestionModal
        open={submittedModalOpen}
        onClose={() => setSubmittedModalOpen(false)}
        selectedQuestion={selectedQuestion}
      />
    </div>
  );
};

export default QuestionBank;
